import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Info, Save, Search, SquareSlash } from "lucide-react";
import { botGet, botPut, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Empty, Loading, PageHeader, Panel, Pill, inputC, primaryBtn } from "@/components/kit";

const PERM_LABELS = {
  ModerateMembers: "Mettere in timeout", KickMembers: "Espellere", BanMembers: "Bannare", ManageMessages: "Gestire i messaggi",
  ManageChannels: "Gestire i canali", ManageGuild: "Gestire il server", ManageNicknames: "Gestire i nickname", ManageRoles: "Gestire i ruoli",
};

export default function Commands() {
  const [commands, setCommands] = useState(null);
  const [disabled, setDisabled] = useState([]);
  const [saved, setSaved] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    botGet("commands").then((list) => {
      setCommands(list);
      const off = list.filter((c) => !c.enabled).map((c) => c.name);
      setDisabled(off); setSaved(off);
    }).catch((e) => { toast.error(apiError(e)); setCommands([]); });
  }, []);
  const dirty = useMemo(() => [...disabled].sort().join() !== [...saved].sort().join(), [disabled, saved]);
  const save = async () => {
    setBusy(true);
    try { await botPut("features/commands", { disabled }); setSaved(disabled); toast.success("Comandi aggiornati", { description: "Su Discord compaiono entro pochi secondi." }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (!commands) return <Loading />;
  const term = q.trim().toLowerCase().replace(/^\//, "");
  const visible = commands.filter((c) => !term || c.name.includes(term) || c.description.toLowerCase().includes(term));
  const cats = [...new Set(visible.map((c) => c.category))];
  const setAll = (cat, on) => {
    const names = commands.filter((c) => c.category === cat).map((c) => c.name);
    setDisabled((d) => (on ? d.filter((n) => !names.includes(n)) : [...new Set([...d, ...names])]));
  };

  return (
    <div className="space-y-6">
      <PageHeader icon={SquareSlash} title="Comandi slash" subtitle={`${commands.length - disabled.length} di ${commands.length} comandi attivi. Si usano su Discord scrivendo / in chat.`}
        actions={<Button onClick={save} disabled={!dirty || busy} className={primaryBtn} data-testid="commands-save"><Save className="mr-2 h-4 w-4" />Salva</Button>} />
      <div className="flex items-start gap-3 rounded-2xl border border-[#5865F2]/30 bg-[#5865F2]/10 p-4 text-sm text-[#c7cbfb]">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        Ogni comando ha dei permessi predefiniti (es. /ban solo per chi può bannare). Puoi cambiarli su Discord: Impostazioni server → Integrazioni → il tuo bot.
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#949BA4]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un comando…" className={`${inputC} pl-9`} data-testid="commands-search" />
      </div>
      {visible.length === 0 && <Empty icon={SquareSlash} title="Nessun comando trovato" />}
      {cats.map((cat) => (
        <Panel key={cat} title={cat} right={
          <div className="flex gap-1">
            <button onClick={() => setAll(cat, true)} className="rounded-lg px-2.5 py-1 text-xs font-semibold text-[#949BA4] hover:bg-white/5 hover:text-white">Attiva tutti</button>
            <button onClick={() => setAll(cat, false)} className="rounded-lg px-2.5 py-1 text-xs font-semibold text-[#949BA4] hover:bg-white/5 hover:text-white">Disattiva tutti</button>
          </div>
        }>
          <div className="divide-y divide-[#232838] overflow-hidden rounded-xl border border-[#232838]">
            {visible.filter((c) => c.category === cat).map((c) => {
              const on = !disabled.includes(c.name);
              return (
                <div key={c.name} className={`flex items-start gap-3 bg-[#131620] px-4 py-3 ${on ? "" : "opacity-50"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-[#8ea0ff]">/{c.name}</span>
                      <span className="text-sm text-[#DBDEE1]">{c.description}</span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {c.options.map((o) => <span key={o.name} title={o.description} className="rounded-md border border-[#232838] bg-[#0f1119] px-1.5 py-0.5 font-mono text-[11px] text-[#B5BAC1]">{o.name}{o.required ? "" : "?"}</span>)}
                      {c.defaultPermission.map((p) => <Pill key={p} tone="yellow">{PERM_LABELS[p] ?? p}</Pill>)}
                    </div>
                  </div>
                  <Switch checked={on} onCheckedChange={(v) => setDisabled((d) => (v ? d.filter((n) => n !== c.name) : [...d, c.name]))} data-testid={`cmd-${c.name}`} />
                </div>
              );
            })}
          </div>
        </Panel>
      ))}
    </div>
  );
}
